import dayjs from 'dayjs';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { formatDateYmd } from '@/utils/dateFormat';
import type { VisitScheduleMassager } from '@/services/main/visitSchedule';
import type { VisitScheduleVisitDisplay } from './components/VisitScheduleWeekCalendar';
import type { VisitDraftPayload, VisitScheduleEvent } from './types';

const dayPickerFormat = parseFormat(JA_JP.format);

export type VisitScheduleMassagerOption = {
  value: number;
  label: string;
};

/** Convert RA2NDatePicker output (和暦 / yyyy年mm月dd日) to ISO YYYY-MM-DD. */
export const convertToIso = (value: string): string => {
  if (!value || !value.trim()) return '';
  const d = parseDate(value.trim(), dayPickerFormat);
  if (Number.isNaN(d.getTime())) return '';
  return formatDateYmd(d);
};

/** RA2: getWeekDate(visitdate) -> [Monday, Sunday] of that week. */
export function getWeekRangeForVisitdate(visitdate: string): {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
} {
  const d = dayjs(visitdate);
  const dayOfWeek = d.day(); // 0=Sun ... 6=Sat
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const start = d.add(diffToMonday, 'day').startOf('day'); // Monday (same week)
  const end = start.add(6, 'day'); // Sunday
  return { start, end };
}

export function nextDraftId(): string {
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isMassagerAvailableOnVisitDate(
  massager: {
    available_from?: string | null;
    available_to?: string | null;
  },
  targetDate: string
): boolean {
  const from = massager.available_from?.trim() || null;
  const to = massager.available_to?.trim() || null;

  if (from && targetDate < from) return false;
  if (to && targetDate > to) return false;

  return true;
}

export function getMassagerSerialSortValue(serialNumber: number | null | undefined): number {
  return typeof serialNumber === 'number' ? serialNumber : Number.MAX_SAFE_INTEGER;
}

export function payloadToCalendarVisit(
  payload: VisitDraftPayload,
  dateStr: string,
  timeStr: string,
  massagerId: number,
  options: { isCopied?: boolean } = {}
): VisitScheduleVisitDisplay {
  const from = dayjs(`${dateStr}T${timeStr}`);
  const minutes = payload.visit_minutes ?? 20;
  const to = from.add(minutes, 'minute');
  return {
    id: nextDraftId(),
    massager_id: massagerId,
    clinic_location_id: payload.clinic_location_id ?? null,
    patient_id: payload.patient_id ?? 0,
    serial_number: payload.serial_number ?? '',
    patient_name: payload.patient_name ?? '—',
    visit_datetime_from: from.format('YYYY-MM-DDTHH:mm:ss'),
    visit_datetime_to: to.format('YYYY-MM-DDTHH:mm:ss'),
    visit_minutes: minutes,
    is_error: payload.is_error ?? false,
    error_messages: payload.error_messages ?? null,
    is_locked: false,
    patient_address_name: payload.patient_address_name,
    patient_base_address1: payload.patient_base_address1,
    patient_base_address2: payload.patient_base_address2,
    patient_base_address3: payload.patient_base_address3,
    patient_base_address_name: payload.patient_base_address_name,
    is_copied: options.isCopied,
    copy_visit_id: payload.copy_visit_id,
    massager_remarks: payload.massager_remarks,
    electric_treatment: payload.electric_treatment,
    is_not_hot_treatment: payload.is_not_hot_treatment,
    is_suddenly_visit: payload.is_suddenly_visit,
    hospital_name: payload.hospital_name,
    doctor_name: payload.doctor_name,
    visit_reason: payload.visit_reason,
    alignment_date: payload.alignment_date,
    patient_suddenly_visit_eligible: payload.patient_suddenly_visit_eligible,
    patient_electric_treatment_eligible: payload.patient_electric_treatment_eligible,
  };
}

export function visitToEvent(v: VisitScheduleVisitDisplay): VisitScheduleEvent {
  return {
    id: v.id,
    patient_id: v.patient_id,
    massager_id: v.massager_id,
    clinic_location_id: v.clinic_location_id ?? null,
    visit_datetime_from: v.visit_datetime_from,
    visit_datetime_to: v.visit_datetime_to,
    visit_minutes: v.visit_minutes,
    patient_name: v.patient_name,
    serial_number: v.serial_number,
    is_draft: typeof v.id === 'string',
    is_locked: v.is_locked,
    patient_address_name: v.patient_address_name ?? undefined,
    patient_base_address1: v.patient_base_address1 ?? undefined,
    patient_base_address2: v.patient_base_address2 ?? undefined,
    patient_base_address3: v.patient_base_address3 ?? undefined,
    patient_base_address_name: v.patient_base_address_name ?? undefined,
    massager_remarks: v.massager_remarks ?? undefined,
    electric_treatment: v.electric_treatment,
    is_not_hot_treatment: v.is_not_hot_treatment,
    is_error: v.is_error,
    error_messages: v.error_messages ?? undefined,
    error_items: v.error_items ?? undefined,
    massager_started_at: v.massager_started_at ?? undefined,
    massager_ended_at: v.massager_ended_at ?? undefined,
    is_suddenly_visit: v.is_suddenly_visit,
    hospital_name: v.hospital_name ?? undefined,
    doctor_name: v.doctor_name ?? undefined,
    visit_reason: v.visit_reason ?? undefined,
    alignment_date: v.alignment_date ?? undefined,
    patient_suddenly_visit_eligible: v.patient_suddenly_visit_eligible,
    patient_electric_treatment_eligible: v.patient_electric_treatment_eligible,
    is_copied: v.is_copied,
    copy_visit_id: v.copy_visit_id,
    same_visit_id: v.same_visit_id,
    group_count: v.group_count,
  };
}

export function removeCalendarVisitAndAdjustGroupCount(
  visits: VisitScheduleVisitDisplay[],
  id: string | number
): VisitScheduleVisitDisplay[] {
  const removed = visits.find(v => v.id === id);
  if (!removed) return visits;

  const sameVisitId = removed.same_visit_id;
  const groupCount = Number(removed.group_count ?? 0);

  return visits
    .filter(v => v.id !== id)
    .map(v => {
      if (sameVisitId == null || v.same_visit_id !== sameVisitId || groupCount <= 0) {
        return v;
      }

      return {
        ...v,
        group_count: Math.max(groupCount - 1, 0),
      };
    });
}

export function getDeparturePositionText(selectedRow: VisitScheduleMassager | null): string {
  if (!selectedRow?.clinic_location) return '';
  const address = `${selectedRow.clinic_location.address1 ?? ''}${selectedRow.clinic_location.address2 ?? ''}`;
  const locationName = selectedRow.clinic_location.location_name ?? '';
  return `${address}${locationName ? `　${locationName}` : ''}`;
}

// Label format (per BD spec):
//   {serial_number} {name} : {address1}{address2}{address3} {location_name}
export function buildMassagerOptions(
  massagersList: VisitScheduleMassager[]
): VisitScheduleMassagerOption[] {
  return massagersList.map(r => {
    const m = r.massager;
    const loc = r.clinic_location;
    const address = [loc?.address1, loc?.address2, loc?.address3].filter(Boolean).join('');
    const locationName = loc?.location_name ?? '';
    const label = `${m?.serial_number ?? ''} ${m?.name ?? ''}　:　${address}${locationName ? `　${locationName}` : ''}`;
    return { value: r.id, label };
  });
}
